import React from 'react';
import { useFieldArray } from 'react-final-form-arrays';

import { ArrowDownwardOutlined, ArrowUpwardOutlined, CopyAllOutlined, DeleteOutlined } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import duplicateBlock from '@wp-block-builder/block-helpers/duplicateBlock';
import { useSetCurrentBlockId } from '@wp-block-builder/editor/hooks';
import { type TBlock } from '@wp-block-builder/package';
import { useEditableBlock } from '@wp-block-builder/views/EditableMessage/hooks';
import { alertInfo, removeSnackbarAlert } from '@wp-react/notifications';

import TuneMenuButton from './TuneMenuButton';
import TuneMenuDivider from './TuneMenuDivider';
import TuneMenuLayout from './TuneMenuLayout';

type Props = {
  name: string;
};
export default function SectionTuneMenu({ name }: Props) {
  const blocksArrayName = name.replace(/\[\d+\]$/, '');
  const { remove, swap, insert, value } = useFieldArray<TBlock>(blocksArrayName).fields;
  const blocks = value ?? [];
  const block = useEditableBlock(name);
  const index = blocks.indexOf(block);
  const isMoveUpDisabled = index === 0;
  const isMoveDownDisabled = index === blocks.length - 1;
  const setCurrentBlockId = useSetCurrentBlockId();

  return (
    <TuneMenuLayout placement="left-start">
      <TuneMenuButton
        placement="left-start"
        tooltipTitle="Duplicate"
        disabled={block.type === 'ContainerYield'}
        onClick={() => {
          if (block.type === 'ContainerYield') {
            return;
          }
          const nBlock = duplicateBlock(block);
          insert(index + 1, nBlock);
          setCurrentBlockId(nBlock.id);
        }}
      >
        <CopyAllOutlined fontSize="small" />
      </TuneMenuButton>

      <TuneMenuButton
        placement="left-start"
        tooltipTitle="Delete"
        disabled={block.type === 'ContainerYield'}
        onClick={() => {
          if (block.type === 'ContainerYield') {
            return;
          }
          remove(index);
          setCurrentBlockId(null);
          const onClick = () => {
            insert(index, block);
            setCurrentBlockId(block.id);
            removeSnackbarAlert();
          };
          alertInfo(
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography color="inherit">Block deleted</Typography>
              <Button onClick={onClick} sx={{ color: 'inherit' }}>
                Undo
              </Button>
            </Stack>
          );
        }}
      >
        <DeleteOutlined fontSize="small" />
      </TuneMenuButton>
      <TuneMenuDivider placement="left-start" />

      <TuneMenuButton
        placement="left-start"
        tooltipTitle="Move up"
        disabled={isMoveUpDisabled}
        onClick={() => {
          swap(index, index - 1);
        }}
      >
        <ArrowUpwardOutlined fontSize="small" />
      </TuneMenuButton>
      <TuneMenuButton
        placement="left-start"
        tooltipTitle="Move down"
        disabled={isMoveDownDisabled}
        onClick={() => {
          swap(index, index + 1);
        }}
      >
        <ArrowDownwardOutlined fontSize="small" />
      </TuneMenuButton>
    </TuneMenuLayout>
  );
}
